import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ProgressTracker from "../../../../utils/ProgressTracker";
import { Checkbox } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  markComplete,
  markDescriptionComplete,
  markNonComplete,
  markNotDescriptionComplete,
  updateTaskHours,
} from "../../../../store/slices/ProjectOngoing/projectOngoingSlice";
import { useSnackbar } from "notistack";
import LoadingSpinner from "./../../../../components/common/loader/LoadingSpinner";
import { fetchAllProjects } from "../../../../store/slices/projectsSlice";
import { fetchDescriptionTasksProjectTracking } from "../../../../store/slices/DescriptionTask";
import { updateOngoingDescription } from "../../../../store/slices/ProjectOngoing/ProjectOngoingUpdateSlice";
import { useCurrencySymbol } from "../../../../utils/currencyUtils";
import DateConversion from "../../../../utils/DateConversion";
import {
  formatAmountComma,
  formatAmountForInput,
  validateAmountValue,
  parseAmountNumber,
} from "../../../../utils/decimalAmountUtils";
import { setSelectedTaskId } from "../../../../store/slices/ProjectOngoing/taskScrollSlice";
import {
  isTrainingExpenseDisplayRow,
  consolidateTrainingExpenseRowsForDisplay,
  applyMyTrainingsDateBoundsToTrainingRows,
  parseSavedOngoingExpenseRaw,
  getOngoingExpenseNumberForTracker,
  filterInstallationOngoingOtherExpenseDisplayRows,
} from "../../../../utils/trainingOtherExpenseSync";

/** Expense input: draft while editing; otherwise saved expenses only (not planned Amount). */
const getOngoingExpenseInputDisplay = (entry, draft) => {
  if (draft != null && String(draft).trim() !== "") return draft;
  const saved = parseSavedOngoingExpenseRaw(entry);
  if (saved == null) return "";
  return formatAmountForInput(saved);
};

const isClientOnlyOngoingDescriptionRow = (entryOrId) => {
  const e =
    typeof entryOrId === "string" ? { _id: entryOrId } : entryOrId || {};
  if (e._isTrainingCost || e._prmSyntheticTrainingExpense || e.readOnly) {
    return true;
  }
  const id = String(e._id ?? "");
  return (
    id.startsWith("training_cost_") ||
    id.startsWith("prm-synthetic") ||
    id.startsWith("prm-training-rollup")
  );
};

const TaskTrackOP = ({
  matchedOpWorkEntries,
  loggedUser,
  row,
  index,
  fetchWorkEntriesProjectTracking,
  setAggregateProgressMap,
  matchedProjectWorkEntries,
  matchedDescriptions,
  setIsLoading,
  showListState,
  isViewOnly = false
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [loadingRows, setLoadingRows] = useState({});
  const currency = useCurrencySymbol();
  const { formatDateByCountry, getDatePlaceholder, getDateFormat } = DateConversion();
  const formatTwoDecimals = (value) =>
    (Math.trunc((Number(value) || 0) * 100) / 100).toFixed(2);

  const { loading, error } = useSelector((state) => state.projectOngoing);
  const selectedTaskId = useSelector((state) => state.taskScroll.selectedTaskId);
  const { mytrainings } = useSelector((state) => state.myTrainings);
  console.log("selectedTaskId", selectedTaskId)

  const [opTrackWorkEntries, setOpTrackWorkEntries] = useState([]);
  useEffect(() => {
    setOpTrackWorkEntries(matchedOpWorkEntries);
  }, [matchedOpWorkEntries]);

  const trainingRollupFromMyTrainings = useMemo(() => {
    const trainingsList = Array.isArray(mytrainings?.trainings)
      ? mytrainings.trainings
      : Array.isArray(mytrainings)
        ? mytrainings
        : [];

    const normKey = (v) => String(v || "").trim().toLowerCase();
    const resolveRefId = (ref) => {
      if (ref == null || ref === "") return "";
      if (typeof ref === "object") return String(ref?._id ?? ref?.id ?? "").trim();
      return String(ref).trim();
    };
    const pick = (obj, keys) => {
      for (const k of keys) {
        const val = obj?.[k];
        if (val != null && String(val).trim() !== "") return val;
      }
      return "";
    };

    if (!row || !loggedUser || trainingsList.length === 0) {
      return { bounds: null, lockedTrainingRow: null };
    }

    const assigneeId = resolveRefId(loggedUser?._id);
    const assigneeEmail = normKey(loggedUser?.EmpEmail ?? loggedUser?.email);
    if (!assigneeId && !assigneeEmail) return { bounds: null, lockedTrainingRow: null };

    const leadLike = row?.lead || row?.Lead || null;
    const projectIdCandidates = new Set(
      [
        resolveRefId(pick(row, ["_id", "id", "ProjectID", "projectid", "projectId", "ProjectId"])),
        resolveRefId(pick(leadLike, ["_id", "id"])),
      ]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const quotationIdCandidates = new Set(
      [
        pick(row, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
        pick(leadLike, ["quotationId", "quotationID", "quotationid", "QuotationId", "QuotationID"]),
      ]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );
    const projectNameCandidates = new Set(
      [pick(row, ["ProjectName", "projectname"]), pick(leadLike, ["ProjectName", "projectname"])]
        .map((x) => normKey(x))
        .filter(Boolean)
    );
    const leadIdCandidates = new Set(
      [pick(row, ["leadid", "leadId", "LeadId", "LeadID"]), pick(leadLike, ["leadid", "leadId", "LeadId", "LeadID"])]
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    );

    const matched = trainingsList.filter((t) => {
      const assignedToRaw = t?.assignedto ?? t?.assignedTo;
      const assignedToId = resolveRefId(assignedToRaw);
      const assignedToEmail =
        typeof assignedToRaw === "string" ? normKey(assignedToRaw) : normKey(t?.assignedToEmail ?? "");
      const matchesAssignee =
        (assigneeId && assignedToId && String(assignedToId) === String(assigneeId)) ||
        (assigneeEmail && assignedToEmail && assignedToEmail === assigneeEmail);
      if (!matchesAssignee) return false;

      const tProjectId = normKey(resolveRefId(t?.projectid ?? t?.projectId ?? t?.project));
      const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? "").trim();
      const tProjectName = normKey(t?.projectname);
      const tLeadId = String(t?.leadid ?? t?.leadId ?? "").trim();

      return (
        (tProjectId && projectIdCandidates.has(tProjectId)) ||
        (tQuotationId && quotationIdCandidates.has(tQuotationId)) ||
        (tProjectName && projectNameCandidates.has(tProjectName)) ||
        (tLeadId && leadIdCandidates.has(tLeadId))
      );
    });

    const parseDate = (d) => {
      if (!d) return null;
      const dt = d instanceof Date ? d : new Date(d);
      return isNaN(dt.getTime()) ? null : dt;
    };

    const normalized = matched.map((t) => {
      const amount = Number(String(t?.trainingcost ?? t?.trainingCost ?? 0).replace(/,/g, ""));
      const notes = t?.notes ?? t?.note ?? "";
      const start =
        parseDate(t?.startdate ?? t?.startDate ?? t?.duedate ?? t?.dueDate ?? t?.DueDate ?? t?.DueDate) ??
        null;
      const end = parseDate(t?.enddate ?? t?.endDate ?? t?.EndDate) ?? null;
      return {
        amount: Number.isFinite(amount) ? amount : 0,
        notes: String(notes || "").trim(),
        start,
        end,
      };
    });

    const startDate =
      normalized
        .map((x) => x.start)
        .filter(Boolean)
        .sort((a, b) => a.getTime() - b.getTime())[0] || null;
    const endDate =
      normalized
        .map((x) => x.end)
        .filter(Boolean)
        .sort((a, b) => b.getTime() - a.getTime())[0] || null;

    const bounds =
      startDate || endDate
        ? { startdate: startDate, enddate: endDate }
        : null;

    const total = normalized.reduce((sum, x) => sum + (Number(x.amount) || 0), 0);
    const joinedNotes = Array.from(new Set(normalized.map((x) => x.notes).filter(Boolean))).join("; ");
    if (total <= 0 && !joinedNotes) {
      return { bounds, lockedTrainingRow: null };
    }

    return {
      bounds,
      lockedTrainingRow: {
        description: "Training Cost",
        value: total,
        notes: joinedNotes,
        startDate,
        endDate,
      },
    };
  }, [mytrainings, row, loggedUser]);

  const { bounds: myTrainingsDateBounds, lockedTrainingRow } = trainingRollupFromMyTrainings;

  const otherExpensesDisplayRows = useMemo(() => {
    const list = filterInstallationOngoingOtherExpenseDisplayRows(
      Array.isArray(matchedDescriptions) ? matchedDescriptions : [],
      row
    );
    const manuals = list.filter((d) => !isTrainingExpenseDisplayRow(d));
    const trains = list.filter((d) => isTrainingExpenseDisplayRow(d));

    if (trains.length > 0) {
      return applyMyTrainingsDateBoundsToTrainingRows(
        consolidateTrainingExpenseRowsForDisplay([...manuals, ...trains]),
        myTrainingsDateBounds
      );
    }

    if (lockedTrainingRow) {
      const sym = String(currency || "").trim();
      const desc = sym ? `Training Cost (${sym})` : "Training Cost";
      return [
        ...manuals,
        {
          _id: `training_cost_mytrainings_fallback_${String(row?._id || "proj")}`,
          description: desc,
          value: lockedTrainingRow.value,
          notes: lockedTrainingRow.notes || "",
          startdate: lockedTrainingRow.startDate,
          enddate: lockedTrainingRow.endDate,
          isTaskComplete: true,
          expenses: lockedTrainingRow.value,
          _isTrainingCost: true,
        },
      ];
    }

    return list;
  }, [
    matchedDescriptions,
    lockedTrainingRow,
    myTrainingsDateBounds,
    row,
    currency,
  ]);

  // const notifiedTasksRef = useRef(new Set());

  // const showDeadlinePopup = useCallback((tasks, message) => {
  //   if (tasks.length > 0) {
  //     // Add task IDs to the notified set
  //     tasks.forEach(task => {
  //       notifiedTasksRef.current.add(task._id);
  //     });

  //     enqueueSnackbar(message, {
  //       variant: "error",
  //       autoHideDuration: 5000, // Longer duration for multiple tasks
  //       anchorOrigin: {
  //         vertical: 'top',
  //         horizontal: 'center',
  //       },
  //     });
  //   }
  // }, [enqueueSnackbar]);

  // useEffect(() => {
  //   console.log("managerWorkEntries", opTrackWorkEntries);

  //   if (opTrackWorkEntries && opTrackWorkEntries.length > 0) {
  //     // Get current date and date for 2 days from now
  //     const now = new Date();
  //     now.setHours(0, 0, 0, 0);

  //     const twoDaysFromNow = new Date();
  //     twoDaysFromNow.setDate(now.getDate() + 2);
  //     twoDaysFromNow.setHours(23, 59, 59, 999);

  //     // Filter entries that end within the next 2 days AND haven't been notified yet
  //     const upcomingDeadlines = opTrackWorkEntries.filter(entry => {
  //       if (!entry.enddate) return false;

  //       // Check if already notified
  //       if (notifiedTasksRef.current.has(entry._id)) {
  //         return false;
  //       }

  //       const endDate = new Date(entry.enddate);
  //       endDate.setHours(0, 0, 0, 0);
  //       return endDate >= now && endDate <= twoDaysFromNow;
  //     });

  //     // Sort by enddate (closest first)
  //     upcomingDeadlines.sort((a, b) => {
  //       return new Date(a.enddate) - new Date(b.enddate);
  //     });

  //     // Show consolidated popup for new upcoming deadlines only
  //     if (upcomingDeadlines.length > 0) {
  //       const taskCount = upcomingDeadlines.length;

  //       // Create a formatted list of tasks
  //       const taskList = upcomingDeadlines.map((entry, index) => {
  //         return `${index + 1}. ${entry.task}`;
  //       }).join('\n');

  //       const message = `⏰ You have ${taskCount} upcoming deadline(s):\n${taskList}`;

  //       // Show single popup with all new tasks
  //       showDeadlinePopup(upcomingDeadlines, message);
  //     }

  //     // Optional: Clean up old notified tasks that are no longer in the list
  //     const currentTaskIds = new Set(opTrackWorkEntries.map(task => task._id));
  //     notifiedTasksRef.current = new Set(
  //       Array.from(notifiedTasksRef.current).filter(id => currentTaskIds.has(id))
  //     );
  //   }
  // }, [opTrackWorkEntries, showDeadlinePopup]);

  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (selectedTaskId) {
      setShouldScroll(true);  // scroll later when rows exist
    }
  }, [selectedTaskId]);


  const refs = useRef({}); // store refs by task id

  useEffect(() => {
    if (
      shouldScroll &&
      selectedTaskId &&
      opTrackWorkEntries.length > 0 &&
      refs.current[selectedTaskId]
    ) {
      refs.current[selectedTaskId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      setShouldScroll(false); // prevent repeating
      dispatch(setSelectedTaskId(null));
    }
  }, [shouldScroll, selectedTaskId, opTrackWorkEntries, dispatch]);


  // useEffect(() => {
  //   // check data is loaded first
  //   if (
  //     selectedTaskId &&
  //     opTrackWorkEntries.length > 0 &&
  //     refs.current[selectedTaskId]
  //   ) {
  //     refs.current[selectedTaskId].scrollIntoView({
  //       behavior: "smooth",
  //       block: "center",
  //     });
  //   }
  // }, [selectedTaskId, opTrackWorkEntries]);



  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
  }, [loading, error, enqueueSnackbar]);

  const [checkedRows] = useState({});

  useEffect(() => {
    if (!opTrackWorkEntries?.length) {
      setAggregateProgressMap({});
      return;
    }
    const progressMap = {};
    opTrackWorkEntries.forEach((entry) => {
      const projectId = entry.projectid;

      if (!progressMap[projectId]) {
        progressMap[projectId] = { totalTasks: 0, completedTasks: 0 };
      }

      progressMap[projectId].totalTasks += 1;
      if (entry.isTaskComplete) {
        progressMap[projectId].completedTasks += 1;
      }
    });

    // calculate percentage for each project
    const aggregateMap = {};
    Object.keys(progressMap).forEach((projectId) => {
      const { totalTasks, completedTasks } = progressMap[projectId];
      aggregateMap[projectId] =
        totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    });

    setAggregateProgressMap(aggregateMap);
  }, [opTrackWorkEntries, setAggregateProgressMap]);

  const [editedTask, setEditedTask] = useState({});
  const [editedNotes, setEditedNotes] = useState({});
  const [editedDescription, setEditedDescription] = useState({});
  const [editedDescriptionNotes, setEditedDescriptionNotes] = useState({});

  const isOverdueIncomplete = (entry) => {
    const isIncomplete =
      entry?.isTaskComplete === false || entry?.isTaskcomplete === false;
    if (!isIncomplete || !entry?.enddate) return false;
    const endDate = new Date(entry.enddate);
    if (isNaN(endDate.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  const handleDescriptionExpensesUpdate = async (entryId) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow({ _id: entryId })) return;
    const value = editedDescription[entryId]; // get the value for this specific task
    if (!entryId) return;
    const updatedData = {
      expenses: parseAmountNumber(value),
    }
    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const handleDescriptionNotesUpdate = async (entryId) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow({ _id: entryId })) return;
    const value = editedDescriptionNotes[entryId]; // get the value for this specific task
    if (!entryId) return;
    const updatedData = {
      notes: value,
    }
    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entryId,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entryId]: false }));
      setIsLoading(false);
    }
  };

  const handleUpdateHours = async (taskId) => {
    if (isViewOnly) return;
    const value = editedTask[taskId]; // get the value for this specific task
    if (!taskId) return;
    const projectid = row?._id;
    const updatedData = {
      hoursspent: Number(value),
    }
    try {
      await dispatch(
        updateTaskHours({
          projectid,
          taskownerid: taskId,
          updatedData
        })
      ).unwrap();
    } catch (err) {
      enqueueSnackbar(err, { variant: "error" });
    } finally {
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    }
  };

  const handleUpdateNotes = async (taskId) => {
    if (isViewOnly) return;
    const value = editedNotes[taskId]; // get the value for this specific task

    if (!taskId) return;
    const projectid = row?._id;
    const updatedData = {
      notes: value,
    }
    try {
      await dispatch(
        updateTaskHours({
          projectid,
          taskownerid: taskId,
          updatedData
        })
      ).unwrap();
    } catch (err) {
      enqueueSnackbar(err, { variant: "error" });
    } finally {
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    }
  };

  const handleTaskMarkComplete = async (entry) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      enqueueSnackbar("Task Completed Successfully", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed To Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const handleMarkNonComplete = async (entry) => {
    if (isViewOnly) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markNonComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      enqueueSnackbar("Task Not-Completed Successfully", {
        variant: "success",
      });
      dispatch(fetchAllProjects());
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  };

  const handlemarkDescriptionComplete = async (entry) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow(entry)) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markDescriptionComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Description Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);

    }
  };

  const handlemarkNotDescriptionComplete = async (entry) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow(entry)) return;
    const projectid = row?._id;
    const taskid = entry?._id;
    setLoadingRows((prev) => ({ ...prev, [entry?._id]: true }));
    setIsLoading(true);
    try {
      await dispatch(markNotDescriptionComplete({ projectid, taskid }));
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
      enqueueSnackbar("Description Not-Completed Successfully", {
        variant: "success",
      });
    } catch {
      enqueueSnackbar("Failed To Non-Complete Task", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);

    }
  };

  const [updatedDateDescription, setUpdatedDateDescription] = useState({})

  const handleDescriptionDateSubmit = useCallback(async (entry) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow(entry)) return;
    const matchedEntry = updatedDateDescription[entry._id] || {};
    const matchedStartDate = matchedEntry.startdate || null;
    const matchedEndDate = matchedEntry.enddate || null;

    const updatedData = {
      description: entry.description,
      value: entry.value,
      notes: entry.notes,
      typeservice: entry.typeservice,
      // startdate: matchedEntry.startdate || matchedStartDate,
      // enddate: matchedEntry.enddate || matchedEndDate,
      startdate: matchedStartDate ?? entry.startdate ?? null,
      enddate: matchedEndDate ?? entry.enddate ?? null,
    };

    try {
      await dispatch(
        updateOngoingDescription({
          projectId: row?._id,
          id: entry._id,
          updatedData,
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  }, [updatedDateDescription, dispatch, row, fetchWorkEntriesProjectTracking, enqueueSnackbar, setLoadingRows, setIsLoading, isViewOnly]);

  const handleDescriptionDateChange = useCallback((date, field, entry) => {
    if (isViewOnly) return;
    if (isClientOnlyOngoingDescriptionRow(entry)) return;
    setUpdatedDateDescription(prev => ({
      ...prev,
      [entry._id]: {
        ...prev[entry._id],
        [field]: date,
      },
    }));

    if (field === "enddate") {
      const newentry = {
        ...entry,
        enddate: date
      }
      handleDescriptionDateSubmit(newentry)
    }
  }, [handleDescriptionDateSubmit, isViewOnly]);




  const [updatedDateWorkEntries, setUpdatedDateWorkEntries] = useState({})

  const handleWorkEntriesDateSubmit = useCallback(async (entry) => {
    if (isViewOnly) return;
    const matchedEntry = updatedDateWorkEntries[entry._id] || {};
    const matchedStartDate = matchedEntry.startdate || null;
    const matchedEndDate = matchedEntry.enddate || null;

    const updatedData = {
      // startdate: matchedEntry.startdate || matchedStartDate,
      // enddate: matchedEntry.enddate || matchedEndDate,
      startdate: matchedStartDate ?? entry.startdate ?? null,
      enddate: matchedEndDate ?? entry.enddate ?? null,
    };
    try {
      await dispatch(
        updateTaskHours({
          projectid: row?._id,
          taskownerid: entry?._id,
          updatedData
        })
      );
      dispatch(fetchWorkEntriesProjectTracking());
      dispatch(fetchDescriptionTasksProjectTracking());
    } catch {
      enqueueSnackbar("Failed To Add Date", { variant: "error" });
    } finally {
      setLoadingRows((prev) => ({ ...prev, [entry?._id]: false }));
      setIsLoading(false);
    }
  }, [updatedDateWorkEntries, row, dispatch, fetchWorkEntriesProjectTracking, enqueueSnackbar, setLoadingRows, setIsLoading, isViewOnly]);

  const workEntriesDateChange = useCallback((date, field, entry) => {
    if (isViewOnly) return;
    setUpdatedDateWorkEntries(prev => ({
      ...prev,
      [entry._id]: {
        ...prev[entry._id],
        [field]: date,
      },
    }));

    if (field === "enddate") {
      const newentry = {
        ...entry,
        enddate: date
      }
      handleWorkEntriesDateSubmit(newentry)
    }
  }, [handleWorkEntriesDateSubmit, isViewOnly]);



  return (
    <div onClick={(e) => (e.stopPropagation())}>
      {showListState === "tasktable" && (
        <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden border">
          {/* Header */}
          <div className=" z-[50] sticky top-0 flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-words whitespace-normal">
            <div className="p-1 w-[10%] text-center"
            >
              Tasks
            </div>
            <div className="p-1 w-[10%] text-center">
            Hours Assigned
            </div>{" "}
            <div className="p-1 w-[10%] text-center">
            Hours Spent
            </div>{" "}
            <div className="p-1 w-[13%] text-center">
              Start Date
            </div>
            <div className="p-1 w-[13%]">End Date</div>
            <div className="p-1 w-[10%]">Notes</div>
            <div className="p-1 w-[24%] text-center">
              Tracker
            </div>
            <div className="p-1 w-[10%] text-center">
              Action
            </div>
          </div>

          <div
            className="flex text-[10px] break-all whitespace-normal max-h-14"
          >
            <div className="p-1 border-b border-[#dee2e6] w-[10%]">
              {"Total"}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%]">
              {opTrackWorkEntries.reduce((sum, item) => sum + (item?.hoursassigned || 0), 0)}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
              {formatTwoDecimals(
                opTrackWorkEntries.reduce(
                  (sum, item) => sum + (item?.hoursspent || 0),
                  0
                )
              )}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
            </div>
            <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
              {opTrackWorkEntries.every((entry) => entry.isTaskComplete) ? (
                <>
                  {(() => {
                    const hoursAssigned = opTrackWorkEntries.reduce(
                      (sum, item) => sum + (item?.hoursassigned || 0),
                      0
                    );
                    const hoursSpent = opTrackWorkEntries.reduce(
                      (sum, item) => sum + (item?.hoursspent || 0),
                      0
                    );

                    const trackerPercentage =
                      hoursAssigned > 0 ? (hoursSpent / hoursAssigned) * 100 : 0;

                    const displayValue = trackerPercentage < 100 ? 100 : trackerPercentage;

                    return <ProgressTracker value={displayValue} />;
                  })()}                   </>
              ) : (
                <>
                  {(() => {
                    const hoursAssigned = opTrackWorkEntries.reduce((sum, item) => sum + (item?.hoursassigned || 0), 0);
                    const hoursSpent = opTrackWorkEntries.reduce((sum, item) => sum + (item?.hoursspent || 0), 0);
                    const trackerPercentage = hoursAssigned > 0 ? (hoursSpent / hoursAssigned) * 100 : 0;
                    return <ProgressTracker value={trackerPercentage} />;
                  })()}
                </>
              )}

            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
            </div>
          </div>

          {opTrackWorkEntries.length > 0 ? (
            opTrackWorkEntries.map((entry, index) => (
              (() => {
                const showOverdueWarning = isOverdueIncomplete(entry);
                return (
              <div
                // ref={(el) => (refs.current[entry._id] = el)}
                key={entry?._id}
                title={showOverdueWarning ? "end date is passed" : ""}
                className={`flex text-[10px] break-all whitespace-normal cursor-pointer ${
                  ""
                }`}
              >
                <div className="p-1 border-b flex flex-col border-[#dee2e6] w-[10%] break-words whitespace-normal">
                  <p className="text-[10px]" title={`${entry.task}`}> {entry.task} </p>
                  {(() => {
                    // Only show ⏳ if task is not complete and enddate is within next 2 days
                    if (entry.enddate && !entry.isTaskComplete) {
                      const endDate = new Date(entry.enddate);
                      const today = new Date();
                      const twoDaysFromNow = new Date();
                      twoDaysFromNow.setDate(today.getDate() + 2);

                      // Set hours to 0 to compare only dates
                      today.setHours(0, 0, 0, 0);
                      endDate.setHours(0, 0, 0, 0);
                      twoDaysFromNow.setHours(0, 0, 0, 0);

                      // Check if enddate is today or within next 2 days
                      if (endDate >= today && endDate <= twoDaysFromNow) {
                        return <span
                          title="You have upcoming deadline in 2 days"
                          className="text-orange-300 text-xs"> ⏳ </span>;
                      }
                    }
                    return null;
                  })()}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                  {entry.hoursassigned}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {entry.isTaskComplete ? (
                    <span>{entry.hoursspent || ""}</span>
                  ) : (
                    <input
                      type="text"
                      disabled={isViewOnly}
                      value={editedTask[entry._id] ?? entry.hoursspent ?? ""}
                      placeholder="enter hours"
                      className={`border border-gray-200 w-full rounded-md max-w-full p-1 text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const value = e.target.value;
                        const parsedValue = Number(value);
                        if (!/^\d*\.?\d{0,2}$/.test(value)) return;
                        if (
                          value !== "" &&
                          parsedValue > Number(entry.hoursassigned || 0)
                        ) {
                          enqueueSnackbar("Hours Spent is exceeding the Hours Assigned", {
                            variant: "error",
                          });
                        }
                        if (value !== "" && parsedValue < 0) return;
                        setEditedTask((prev) => ({
                          ...prev,
                          [entry._id]: value, // store by task id
                        }));
                      }}
                      onBlur={() => handleUpdateHours(entry._id)}
                    />
                  )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
                  {entry.isTaskComplete ? (
                    <div className="text-black">
                      {entry.startdate
                        ? formatDateByCountry(entry.startdate)
                        : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        entry.startdate
                          ? new Date(entry.startdate)
                          : row?.DateRequested
                            ? new Date(row.DateRequested)
                            : null
                      }
                      maxDate={new Date()}
                      selected={
                        updatedDateWorkEntries[entry._id]?.startdate
                          ? updatedDateWorkEntries[entry._id].startdate
                          : entry.startdate
                            ? new Date(entry.startdate)
                            : null
                      } onChange={(date) => workEntriesDateChange(date, 'startdate', entry)}
                    />)}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
                  {(entry.isTaskComplete) ? (
                    <div className="text-black">
                      {entry.enddate
                        ? formatDateByCountry(entry.enddate)
                        : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 ${
                        showOverdueWarning ? "border-orange-400" : "border-gray-300"
                      } rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        updatedDateWorkEntries[entry._id]?.startdate
                          ? new Date(updatedDateWorkEntries[entry._id].startdate)
                          : entry.startdate
                            ? new Date(entry.startdate)
                            : null
                      }
                      selected={
                        updatedDateWorkEntries[entry._id]?.enddate
                          ? updatedDateWorkEntries[entry._id].enddate
                          : entry.enddate
                            ? new Date(entry.enddate)
                            : null
                      } onChange={(date) => workEntriesDateChange(date, 'enddate', entry)}
                    />)}
                </div>
                <div
                  title={`${entry.notes}`}
                  className="p-1 border-b border-[#dee2e6] w-[10%] text-center break-words whitespace-normal">
                  {/* {entry.notes || ""} */}
                  {entry.isTaskComplete ? (
                    <span>{entry.notes || ""}</span>
                  ) : (
                    <input
                      type="text"
                      disabled={isViewOnly}
                      value={editedNotes[entry._id] ?? entry.notes ?? ""}
                      placeholder="enter note"
                      className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const value = e.target.value;
                        setEditedNotes((prev) => ({
                          ...prev,
                          [entry._id]: value,
                        }));
                      }}
                      onBlur={() => handleUpdateNotes(entry._id)}
                    />
                  )}
                </div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                  {entry.isTaskComplete ?
                    <ProgressTracker
                      value={
                        (() => {
                          const rawValue =
                            ((Number(entry.hoursspent) || 0) /
                              entry.hoursassigned) *
                            100;
                          return rawValue < 100 ? 100 : rawValue;
                        })()
                      }
                    />
                    :
                    <ProgressTracker
                      value={
                        ((Number(editedTask[entry._id] ?? entry.hoursspent) || 0) /
                          entry.hoursassigned) *
                        100
                      }
                    />
                  }
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center cursor-pointer">
                  {loadingRows[index] ? (
                    <LoadingSpinner />
                  ) : (
                    <Checkbox
                      disabled={
                        isViewOnly ||
                        row.Assignedperson?.every(person => person.isProjectComplete === true) ||
                        (((Number(editedTask[entry._id] ?? entry.hoursspent) || 0) /
                          entry.hoursassigned) *
                          100) < 1
                      }
                      checked={entry.isTaskComplete || !!checkedRows[index]}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const isChecked = e.target.checked;

                        const currentHours = Number(
                          editedTask[entry._id] ?? entry.hoursspent
                        );

                        const startDate =
                          updatedDateWorkEntries[entry._id]?.startdate || entry.startdate;
                        const endDate =
                          updatedDateWorkEntries[entry._id]?.enddate || entry.enddate;

                        if (isChecked) {
                          if (!currentHours || currentHours <= 0) {
                            enqueueSnackbar(
                              "Please enter hours spent before completing task",
                              { variant: "error" }
                            );
                            e.preventDefault();
                            return;
                          }
                          if (!startDate || !endDate) {
                            enqueueSnackbar("Please Select End Date", {
                              variant: "error",
                            });
                            return;
                          }
                          handleTaskMarkComplete(entry, index);
                          handleWorkEntriesDateSubmit(entry)
                        } else {
                          handleMarkNonComplete(entry);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            );
              })()
            ))
          ) : (
            <div className="flex text-[14px] break-all whitespace-normal max-h-14 p-1">
              <p className="text-red-500 text-center">
                {" "}
                No Tracking Task Entries Found
              </p>
            </div>
          )}
        </div>
      )}


      {showListState === "otherexpenses" && (
        <div className="w-full max-h-[24rem] overflow-y-auto overflow-x-hidden border">
          <div className="z-[50] sticky top-0 flex bg-[#e8e8e8] text-black text-[10px] font-semibold break-words whitespace-normal">
            <div className="p-1 w-[10%] text-center">
              Description
            </div>
            <div className="p-1 w-[10%] text-center">
              Amount ({currency})
            </div>
            <div className="p-1 w-[13%] text-center">
              Start Date
            </div>
            <div className="p-1 w-[13%] text-center">
              End Date
            </div>
            <div className="p-1 w-[10%] text-center">
              Expenses ({currency})
            </div>
            <div className="p-1 w-[10%]">Notes</div>
            <div className="p-1 w-[24%] text-center">
              Tracker
            </div>
            <div className="p-1 w-[10%] text-center">
              Action
            </div>
          </div>


          <div
            className="flex text-[10px] break-all whitespace-normal max-h-14"
          >
            <div className="p-1 text-center border-b border-[#dee2e6] w-[10%]">
              {"Total"}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%]">
              {formatAmountComma(
                otherExpensesDisplayRows.reduce(
                  (sum, item) => sum + parseAmountNumber(item?.value),
                  0
                ) || 0
              )}
            </div>

            <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
              {formatAmountComma(
                otherExpensesDisplayRows.reduce(
                  (sum, item) => sum + parseAmountNumber(item?.expenses),
                  0
                ) || 0
              )}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
            </div>
            <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
              {(() => {
                const list = otherExpensesDisplayRows;

                const hoursAssigned = list.reduce(
                  (sum, item) => sum + parseAmountNumber(item?.value),
                  0
                );
                const hoursSpent = list.reduce(
                  (sum, item) => sum + parseAmountNumber(item?.expenses),
                  0
                );

                const trackerPercentage =
                  hoursAssigned > 0 ? (hoursSpent / hoursAssigned) * 100 : 0;

                const displayValue = list.every(
                  (entry) =>
                    isClientOnlyOngoingDescriptionRow(entry) ||
                    (entry.isTaskComplete &&
                      entry.enddate &&
                      parseSavedOngoingExpenseRaw(entry) != null)
                )
                  ? trackerPercentage < 100
                    ? 100
                    : trackerPercentage
                  : trackerPercentage;

                return <ProgressTracker value={displayValue} />;
              })()}
            </div>
            <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
            </div>
          </div>

          {otherExpensesDisplayRows.length > 0 ? (
            otherExpensesDisplayRows.map((entry, index) => (
              (() => {
                const showOverdueWarning = isOverdueIncomplete(entry);
                const readOnlyTraining = isClientOnlyOngoingDescriptionRow(entry);
                const isTrulyComplete =
                  !readOnlyTraining &&
                  entry.isTaskComplete &&
                  entry.enddate &&
                  parseSavedOngoingExpenseRaw(entry) != null;
                const displayComplete = readOnlyTraining || isTrulyComplete;
                return (
              <div
                key={entry._id || `other-exp-${index}`}
                title={showOverdueWarning ? "end date is passed" : ""}
                className={`flex text-[10px] break-words whitespace-normal cursor-default ${
                  ""
                }`}
              >
                <div className="p-1 border-b border-[#dee2e6] w-[10%]"
                  title={`${entry.description}`}
                >
                  {entry.description}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%]">
                  {formatAmountComma(entry.value)}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
                  {displayComplete ? (
                    <div className="text-black">
                      {entry.startdate
                        ? formatDateByCountry(entry.startdate)
                        : readOnlyTraining && row?.DateRequested
                          ? formatDateByCountry(row.DateRequested)
                          : ""}
                    </div>
                  ) : (
                    <DatePicker
                      dateFormat={getDateFormat()}
                      disabled={isViewOnly}
                      className={`w-full h-full text-center outline-none border p-1 border-gray-300 rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                      placeholderText={getDatePlaceholder()}
                      popperClassName="z-[1500]"
                      minDate={
                        entry.startdate
                          ? new Date(entry.startdate)
                          : row?.DateRequested
                            ? new Date(row.DateRequested)
                            : null
                      }
                      maxDate={new Date()}
                      selected={
                        updatedDateDescription[entry._id]?.startdate
                          ? updatedDateDescription[entry._id].startdate
                          : entry.startdate
                            ? new Date(entry.startdate)
                            : null
                      } onChange={(date) => handleDescriptionDateChange(date, 'startdate', entry)}
                    />)}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[13%] text-center">
                  {displayComplete ? (
                      <div className="text-black">
                        {entry.enddate
                          ? formatDateByCountry(entry.enddate)
                          : ""}
                      </div>
                    ) : (
                      <DatePicker
                        dateFormat={getDateFormat()}
                        disabled={isViewOnly}
                        className={`w-full h-full text-center outline-none border p-1 ${
                          showOverdueWarning ? "border-orange-400" : "border-gray-300"
                        } rounded-md ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : "bg-white"}`}
                        placeholderText={getDatePlaceholder()}
                        popperClassName="z-[1500]"
                        minDate={
                          updatedDateDescription[entry._id]?.startdate
                            ? new Date(updatedDateDescription[entry._id].startdate)
                            : entry.startdate
                              ? new Date(entry.startdate)
                              : null
                        }
                        selected={
                          updatedDateDescription[entry._id]?.enddate
                            ? updatedDateDescription[entry._id].enddate
                            : entry.enddate
                              ? (() => {
                                  if (entry.enddate instanceof Date) {
                                    return isNaN(entry.enddate.getTime()) ? null : entry.enddate;
                                  }
                                  const date = new Date(entry.enddate);
                                  return isNaN(date.getTime()) ? null : date;
                                })()
                              : null
                        } onChange={(date) => handleDescriptionDateChange(date, 'enddate', entry)}
                      />
                    )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center">
                  {displayComplete ? (
                      <span>{formatAmountComma(entry.expenses ?? entry.value) || ""}</span>
                    ) : (
                      <input
                        type="text"
                        disabled={isViewOnly}
                        value={getOngoingExpenseInputDisplay(
                          entry,
                          editedDescription[entry._id]
                        )}
                        placeholder="enter expense spent"
                        className={`border border-gray-200 w-full rounded-md max-w-full p-1 text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                        onChange={(e) => {
                          if (isViewOnly) return;
                          const value = validateAmountValue(e.target.value);
                          const amountLimit = parseAmountNumber(entry.value);
                          const expenseNum = parseAmountNumber(value);
                          if (
                            value !== "" &&
                            amountLimit > 0 &&
                            expenseNum > amountLimit
                          ) {
                            enqueueSnackbar("Expenses is exceeding the Amount", {
                              variant: "error",
                            });
                          }
                          if (expenseNum < 0) return;
                          setEditedDescription((prev) => ({
                            ...prev,
                            [entry._id]: value, // store by task id
                          }));
                        }}
                        onBlur={() => handleDescriptionExpensesUpdate(entry._id)}
                      />
                    )}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center break-all whitespace-normal "
                  title={`${entry.notes}`}
                >
                  {/* {entry.notes} */}
                  {displayComplete ? (
                    <span>{entry.notes || ""}</span>
                  ) : (
                    <input
                      type="text"
                      disabled={isViewOnly}
                      value={editedDescriptionNotes[entry._id] ?? entry.notes ?? ""}
                      placeholder="enter note"
                      className={`border border-gray-200 w-full max-w-full p-1 rounded-md text-center ${isViewOnly ? "bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed" : ""}`}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const value = e.target.value;
                        setEditedDescriptionNotes((prev) => ({
                          ...prev,
                          [entry._id]: value,
                        }));
                      }}
                      onBlur={() => handleDescriptionNotesUpdate(entry._id)}
                    />
                  )}
                </div>
                <div className="p-2.5 border-b border-[#dee2e6] w-[24%] text-center">
                  {readOnlyTraining ? (
                    <ProgressTracker value={100} />
                  ) : (() => {
                    const isTrulyComplete =
                      entry.isTaskComplete &&
                      entry.enddate &&
                      parseSavedOngoingExpenseRaw(entry) != null;
                    return isTrulyComplete ? (
                      <ProgressTracker
                        value={
                          (() => {
                            const rawValue =
                              (getOngoingExpenseNumberForTracker(
                                entry,
                                editedDescription[entry._id]
                              ) /
                                entry.value) *
                              100;
                            return rawValue < 100 ? 100 : rawValue;
                          })()
                        }
                      />
                    ) : (
                      <ProgressTracker
                        value={
                          (getOngoingExpenseNumberForTracker(
                            entry,
                            editedDescription[entry._id]
                          ) /
                            entry.value) *
                          100
                        }
                      />
                    );
                  })()}
                </div>
                <div className="p-1 border-b border-[#dee2e6] w-[10%] text-center cursor-pointer">
                  {loadingRows[index] ? (
                    <LoadingSpinner />
                  ) : (

                    <Checkbox
                      disabled={
                        isViewOnly ||
                        readOnlyTraining ||
                        row.Assignedperson?.every(person => person.isProjectComplete === true) ||
                        ((getOngoingExpenseNumberForTracker(
                          entry,
                          editedDescription[entry._id]
                        ) /
                          entry.value) *
                          100) < 1
                      }
                      checked={(() => {
                        return (
                          readOnlyTraining ||
                          (entry.isTaskComplete &&
                            entry.enddate &&
                            parseSavedOngoingExpenseRaw(entry) != null)
                        );
                      })()}
                      onChange={(e) => {
                        if (isViewOnly) return;
                        const isChecked = e.target.checked;
                        const currentExpenses = getOngoingExpenseNumberForTracker(
                          entry,
                          editedDescription[entry._id]
                        );
                        const startDate =
                          updatedDateDescription[entry._id]?.startdate || entry.startdate;
                        const endDate =
                          updatedDateDescription[entry._id]?.enddate || entry.enddate;

                        if (isChecked) {
                          if (!startDate || !endDate) {
                            enqueueSnackbar("Please Select End Date", {
                              variant: "error",
                            });
                            return;
                          }

                          if (!currentExpenses || currentExpenses <= 0) {
                            enqueueSnackbar("Please Enter Expense", {
                              variant: "error",
                            });
                            return;
                          }

                          handlemarkDescriptionComplete(entry, index);
                          handleDescriptionDateSubmit(entry);
                        } else {
                          handlemarkNotDescriptionComplete(entry);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            );
              })()
            ))
          ) : (
            <div className="flex text-[14px] break-words whitespace-normal max-h-14 p-1">
              <p className="text-red-500 text-center">
                {" "}
                No Tracking Task Entries Found
              </p>
            </div>
          )}

        </div>
      )}


      {matchedProjectWorkEntries.length > 0 &&
        matchedProjectWorkEntries.every((entry) => entry.isTaskComplete) && (
          <div className="flex justify-end p-2">
            {/* <button
              onClick={handleCompleteProject}
              className="bg-blue-500  hover:bg-blue-700 py-1 px-4 text-white rounded-md"
            >
              Complete
            </button> */}
          </div>
        )}
    </div>
  );
};

export default TaskTrackOP;
